import { AbsoluteModelFieldPath } from "../../../../../Common/ModelPath/AbsoluteModelFieldPath";
import { AbsoluteResolvedModelPath } from "../../../../../Common/ModelPath/ModelPath";
import { ILazyLoadingManager } from "../LazyLoading/LazyLoadingManager";
import { Map2 } from "../../../../../Common/ModelPath/Map/Map2";
import { DataDeclarationHelper } from "../DataDeclaration/DataDeclarationHelper";
import { getOrAdd } from "../../../../../Common/CollectionUtils";
import { Set2 } from "../../../../../Common/ModelPath/Set/Set2";
import { BindingPath } from "../CoreTypes";
import { arrayIsNotEmpty } from "../../../../../Common/TypingUtils";

import { MultilineManager } from "./MultilineManager";
import { IFilteringStrategy } from "./IFilteringStrategy";

export interface ISimpleModelAccessor {
    getChildren(bindingPath: BindingPath | AbsoluteResolvedModelPath): readonly string[] | undefined;
}

export class FilteringManager {
    private readonly lazyLoadingManager: ILazyLoadingManager;
    private readonly sendToServerAction: () => Promise<void>;
    private readonly serverSideFilteringStrategy: IFilteringStrategy;
    private readonly clientSideFilteringStrategy: IFilteringStrategy;
    private readonly multilineManagers = new Map<
        number,
        Map2<AbsoluteResolvedModelPath, Map<string, MultilineManager>>
    >();
    private readonly dataDeclarationHelper: DataDeclarationHelper;
    private readonly modelAccessorFactory: () => ISimpleModelAccessor;
    private readonly currentRedefinedLayerIndexProvider: () => number | undefined;

    public constructor(
        dataDeclarationHelper: DataDeclarationHelper,
        lazyLoadingManager: ILazyLoadingManager,
        sendToServerAction: () => Promise<void>,
        clientSideFilteringStrategy: IFilteringStrategy,
        serverSideFilteringStrategy: IFilteringStrategy,
        modelAccessorFactory: () => ISimpleModelAccessor,
        currentRedefinedLayerIndexProvider: () => number | undefined
    ) {
        this.currentRedefinedLayerIndexProvider = currentRedefinedLayerIndexProvider;
        this.modelAccessorFactory = modelAccessorFactory;
        this.dataDeclarationHelper = dataDeclarationHelper;
        this.lazyLoadingManager = lazyLoadingManager;
        this.sendToServerAction = sendToServerAction;
        this.clientSideFilteringStrategy = clientSideFilteringStrategy;
        this.serverSideFilteringStrategy = serverSideFilteringStrategy;
    }

    public notifyWithChanges(changedPathFields: Set2<AbsoluteModelFieldPath>) {
        const managersForCurrentLayer = this.getMultilineManagersByCurrentLayer();
        Iterator.from(managersForCurrentLayer.values() ?? [])
            .flatMap(x => x.values())
            .forEach(x => x.notifyWithChanges(changedPathFields));
    }

    public async requestFilteringAllMultilines() {
        const allAwaitingTasks = Iterator.from(this.multilineManagers.values())
            .flatMap(x => x.values())
            .flatMap(x => x.values())
            .map(x => {
                x.requestFiltering();
                return x.waitForPendingOperations();
            })
            .toArray();
        await Promise.all(allAwaitingTasks);
    }

    public handleAddInstances(
        bindingPath: AbsoluteResolvedModelPath,
        instances: readonly string[],
        insertAfterInstance?: string,
        insertBeforeInstance?: string
    ) {
        const managersByPath = this.getMultilineManagersByCurrentLayer().getOrSet(
            bindingPath,
            () => new Map<string, MultilineManager>()
        );
        for (const manager of managersByPath.values()) {
            manager.handleAddInstances(instances, insertAfterInstance, insertBeforeInstance);
        }
    }

    public handleRemoveInstances(bindingPath: AbsoluteResolvedModelPath, instances: readonly string[]) {
        const managersByPath = this.getMultilineManagersByCurrentLayer().getOrSet(
            bindingPath,
            () => new Map<string, MultilineManager>()
        );
        for (const manager of managersByPath.values()) {
            manager.handleRemoveInstances(instances);
        }
    }

    public handleRedefinedModelMerged() {
        const managers = Iterator.from(this.getMultilineManagersByCurrentLayer().values()).flatMap(x => x.values());
        for (const manager of managers) {
            const state = manager.getCurrentState();
            const hasActiveFiltersOrSorting = arrayIsNotEmpty(state.filters) || state.sorting != undefined;
            manager.requestFiltering(hasActiveFiltersOrSorting);
        }
    }

    public getMultilineManager(bindingPath: AbsoluteResolvedModelPath, filteringUniqueId: string): MultilineManager {
        const managersByLayer = this.getMultilineManagersByCurrentLayer();
        const managersByPath = managersByLayer.getOrSet(bindingPath, () => new Map<string, MultilineManager>());
        const manager = getOrAdd(
            managersByPath,
            filteringUniqueId,
            () =>
                new MultilineManager(
                    this.dataDeclarationHelper,
                    this.lazyLoadingManager,
                    this.sendToServerAction,
                    this.clientSideFilteringStrategy,
                    this.serverSideFilteringStrategy,
                    this.modelAccessorFactory(),
                    bindingPath,
                    filteringUniqueId
                )
        );
        return manager;
    }

    public resetAllMultilinePages() {
        const managers = Iterator.from(this.getMultilineManagersByCurrentLayer().values()).flatMap(x => x.values());
        for (const manager of managers) {
            manager.setCurrentPage(1);
        }
    }

    private getRedefinedModelLayerIndex() {
        return this.currentRedefinedLayerIndexProvider() ?? Number.MIN_SAFE_INTEGER;
    }

    private getMultilineManagersByLayer(redefinedModelIndex: number) {
        const managersByLayer = getOrAdd(
            this.multilineManagers,
            redefinedModelIndex,
            () => new Map2<AbsoluteResolvedModelPath, Map<string, MultilineManager>>()
        );
        return managersByLayer;
    }

    private getMultilineManagersByCurrentLayer() {
        return this.getMultilineManagersByLayer(this.getRedefinedModelLayerIndex());
    }
}
